/**
 * Route Configuration
 * Centralized route definitions for all pages in the application
 *
 * This file defines:
 * - All routes with their layout requirements
 * - Navigation structure (what appears where)
 * - Auth/permission requirements
 * - Breadcrumb hierarchy
 */

import {
  LayoutDashboard,
  FileText,
  Video,
  Phone,
  User,
  Settings,
  Shield,
  Radio,
  MessageSquare,
  PlayCircle,
  Home,
  Users,
  Code,
  BookOpen,
  LogIn,
  UserPlus,
  Mail,
  ClipboardList,
  Bell,
  Key,
  BarChart3,
  Activity,
  CreditCard,
  DollarSign,
  List,
  Clock,
  TrendingUp,
  AlertTriangle,
  Database,
  // Call Metrics icons
  PhoneCall,
  Timer,
  CheckCircle,
  Wifi,
  GitBranch,
  Lightbulb,
  Mic,
  StickyNote,
  Box,
  Workflow,
  PenTool,
  Map,
  FolderOpen,
  FileEdit,
  ThumbsUp,
  Eye,
  Star,
  ClipboardCheck,
  Zap,
  Server,
  Gauge,
  // Infrastructure icons
  Globe,
  // Access Control icons
  Lock,
  Layers,
  History,
  UserCog,
  KeyRound,
  ShieldCheck,
  ScrollText,
  Award,
  Tags,
} from 'lucide-react';
import type { RouteConfig } from './types';
import { resolveRunbook } from '@/lib/runbook/client';

// ============================================================================
// PUBLIC PAGES
// ============================================================================

const publicRoutes: RouteConfig[] = [
  {
    id: 'home',
    path: '/',
    title: 'EngageIQ - AI-Enhanced Response System',
    description: resolveRunbook('ui-frontend.routes.config.description', 'AI-Enhanced Non-Emergency Response System for Law Enforcement'),
    layout: 'public',
    header: 'hidden',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'public',
    auth: 'none',
    hideBreadcrumb: true,
  },
  {
    id: 'developers',
    path: '/developers',
    title: 'Developer Portal',
    description: 'API documentation and developer resources',
    layout: 'developer',
    header: 'developer',
    footer: 'minimal',
    sidebar: 'none',
    portal: 'developer',
    auth: 'none',
    hideBreadcrumb: true,
    requiredFeatures: ['preview_beta'],
  },
  // =========================================================================
  // Alpha Preview Routes (Stub Pages)
  // =========================================================================
  {
    id: 'help',
    path: '/help',
    title: 'Help Center',
    description: 'Get help and support',
    layout: 'default',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'public',
    auth: 'none',
    requiredFeatures: ['preview_alpha'],
  },
  {
    id: 'privacy',
    path: '/privacy',
    title: 'Privacy Policy',
    description: 'Our privacy policy and data practices',
    layout: 'default',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'public',
    auth: 'none',
    requiredFeatures: ['preview_alpha'],
  },
  {
    id: 'terms',
    path: '/terms',
    title: 'Terms of Service',
    description: 'Terms and conditions of service',
    layout: 'default',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'public',
    auth: 'none',
    requiredFeatures: ['preview_alpha'],
  },
  {
    id: 'feedback',
    path: '/feedback',
    title: 'Send Feedback',
    description: 'Share your feedback with us',
    layout: 'default',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'public',
    auth: 'optional',
    requiredFeatures: ['preview_alpha'],
  },
  {
    id: 'accessibility',
    path: '/accessibility',
    title: 'Accessibility',
    description: 'Accessibility statement and features',
    layout: 'default',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'public',
    auth: 'none',
    requiredFeatures: ['preview_alpha'],
  },
];

// ============================================================================
// AUTH PAGES
// ============================================================================

const authRoutes: RouteConfig[] = [
  {
    id: 'login',
    path: '/login',
    title: resolveRunbook('ui-frontend.routes.officer_login_title', 'Officer Login'),
    description: resolveRunbook('ui-frontend.routes.officer_login_desc', 'Sign in to the Officer Portal'),
    layout: 'auth',
    header: 'hidden',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'none',
    authRedirect: '/dashboard', // Redirect here if already logged in
    hideBreadcrumb: true,
  },
  {
    id: 'citizen-login',
    path: '/citizen/login',
    title: resolveRunbook('ui-frontend.routes.citizen_login_title', 'Citizen Login'),
    description: resolveRunbook('ui-frontend.routes.citizen_login_desc', 'Sign in to the Citizen Portal'),
    layout: 'auth',
    header: 'hidden',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'citizen',
    auth: 'none',
    authRedirect: '/citizen',
    hideBreadcrumb: true,
  },
  {
    id: 'citizen-register',
    path: '/citizen/register',
    title: 'Create Account',
    description: 'Register for the Citizen Portal',
    layout: 'auth',
    header: 'hidden',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'citizen',
    auth: 'none',
    hideBreadcrumb: true,
  },
  {
    id: 'citizen-verify-email',
    path: '/citizen/verify-email',
    title: 'Verify Email',
    description: 'Verify your email address',
    layout: 'auth',
    header: 'hidden',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'citizen',
    auth: 'none',
    hideBreadcrumb: true,
  },
];

// ============================================================================
// CITIZEN PORTAL
// ============================================================================

const citizenRoutes: RouteConfig[] = [
  {
    id: 'citizen-portal',
    path: '/citizen',
    title: resolveRunbook('ui-frontend.routes.citizen_portal_title', 'Citizen Portal'),
    description: resolveRunbook('ui-frontend.routes.citizen_portal_desc', 'Report incidents and check status'),
    layout: 'citizen',
    header: 'citizen',
    footer: 'minimal',
    sidebar: 'none',
    portal: 'citizen',
    auth: 'optional', // Can view without auth, different experience if logged in
    hideBreadcrumb: true,
  },
];

// ============================================================================
// DASHBOARD PAGES (Officer Portal)
// ============================================================================

const dashboardRoutes: RouteConfig[] = [
  {
    id: 'dashboard',
    path: '/dashboard',
    title: 'Calendar',
    description: 'Weekly calendar command center for managing calls and incidents',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbLabel: 'Calendar',
    nav: {
      label: 'Calendar',
      icon: LayoutDashboard,
      group: 'navigation',
      positions: ['main', 'sidebar', 'mobile', 'user-menu'],
      order: 1,
    },
  },
  {
    id: 'reports',
    path: '/reports',
    title: 'Incident Reports',
    description: 'View and manage incident reports',
    layout: 'dashboard',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Reports',
    nav: {
      label: 'Reports',
      icon: FileText,
      group: 'navigation',
      positions: ['main', 'sidebar', 'mobile', 'user-menu'],
      order: 2,
    },
  },
  {
    id: 'reports-detail',
    path: '/reports/[id]',
    title: (params) => `Report #${params.params.id?.slice(0, 8) || 'Unknown'}`,
    description: 'View incident report details',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'reports',
    breadcrumbLabel: (params) => `Case #${params.params.id?.slice(0, 8) || '...'}`,
  },
  {
    id: 'reports-new',
    path: '/reports/new',
    title: 'Create Report',
    description: 'Create a standalone incident report',
    layout: 'settings',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'reports',
    breadcrumbLabel: 'Create Report',
  },
  {
    id: 'reports-template-editor',
    path: '/reports/template-editor',
    title: 'Template Editor',
    description: 'Edit report templates',
    layout: 'settings',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'reports',
    breadcrumbLabel: 'Template Editor',
  },
  {
    id: 'profile',
    path: '/profile',
    title: 'Profile Settings',
    description: 'Manage your account and preferences',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'required',
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Profile',
    nav: {
      label: 'Profile',
      icon: User,
      group: 'settings',
      positions: ['user-menu'],
      order: 1,
    },
  },
  {
    id: 'notifications',
    path: '/notifications',
    title: 'Notifications',
    description: 'View and manage your notifications',
    layout: 'dashboard',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'required',
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Notifications',
    nav: {
      label: 'Notifications',
      icon: Bell,
      group: 'navigation',
      positions: ['sidebar', 'user-menu'],
      order: 10,
    },
  },
  {
    id: 'call-feedback',
    path: '/call-feedback',
    title: 'Call Feedback',
    description: 'Provide feedback on completed calls',
    layout: 'minimal',
    header: 'hidden',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'optional',
    hideBreadcrumb: true,
  },
  // =========================================================================
  // New Calls Section Pages
  // =========================================================================
  {
    id: 'active-calls',
    path: '/active-calls',
    title: 'Active Calls',
    description: 'Monitor and join ongoing calls (redirects to calendar with active filter)',
    layout: 'dashboard',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Active Calls',
    // Removed from nav - absorbed into calendar view
  },
  {
    id: 'call-history',
    path: '/history',
    title: 'Call History',
    description: 'Browse and search completed calls (redirects to calendar)',
    layout: 'dashboard',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Call History',
    // Removed from nav - absorbed into calendar view
  },
  // =========================================================================
  // Library Section Pages
  // =========================================================================
  {
    id: 'files',
    path: '/files',
    title: 'Files & Folders',
    description: 'Browse and manage all files',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Files & Folders',
    nav: {
      label: 'Files & Folders',
      icon: FolderOpen,
      group: 'navigation',
      positions: ['sidebar'],
      order: 8,
    },
  },
  {
    id: 'knowledge-base',
    path: '/knowledge-base',
    title: 'Knowledge Base',
    description: 'Search and manage documents for AI retrieval',
    layout: 'dashboard',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Knowledge Base',
    nav: {
      label: 'Knowledge Base',
      icon: BookOpen,
      group: 'navigation',
      positions: ['sidebar'],
      order: 9,
    },
  },
  {
    id: 'search',
    path: '/search',
    title: 'Search',
    description: 'Cross-content search across calls, entities, knowledge, and support history',
    layout: 'dashboard',
    header: 'full',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Search',
  },
];

// ============================================================================
// TRAINING PORTAL PAGES
// ============================================================================

const trainingRoutes: RouteConfig[] = [
  {
    id: 'training',
    path: '/training',
    title: 'Training',
    description: 'Training portal — practice calls, scores, and achievements',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    breadcrumbLabel: 'Training',
    nav: {
      label: 'Training',
      icon: TrendingUp,
      group: 'navigation',
      positions: ['sidebar'],
      order: 10,
    },
  },
  {
    id: 'training-dashboard',
    path: '/training/dashboard',
    title: 'My Dashboard',
    description: 'Personal training performance dashboard',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    breadcrumbParent: 'training',
    breadcrumbLabel: 'Dashboard',
  },
  {
    id: 'training-dashboard-department',
    path: '/training/dashboard/department',
    title: 'Department Dashboard',
    description: 'Department-wide training performance dashboard',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    breadcrumbParent: 'training-dashboard',
    breadcrumbLabel: 'Department',
  },
  {
    id: 'training-dashboard-agency',
    path: '/training/dashboard/agency',
    title: 'Agency Dashboard',
    description: 'Agency-wide training performance dashboard',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    breadcrumbParent: 'training-dashboard',
    breadcrumbLabel: 'Agency',
  },
  {
    id: 'training-achievements',
    path: '/training/achievements',
    title: 'Achievements',
    description: 'Browse and track training achievements',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    breadcrumbParent: 'training',
    breadcrumbLabel: 'Achievements',
    nav: {
      label: 'Achievements',
      icon: Award,
      group: 'navigation',
      positions: ['sidebar'],
      order: 11,
    },
  },
  {
    id: 'training-manage-achievements',
    path: '/training/manage-achievements',
    title: 'Manage Achievements',
    description: 'Create and manage custom training achievements',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    breadcrumbParent: 'training-achievements',
    breadcrumbLabel: 'Manage',
  },
  {
    id: 'training-leaderboards',
    path: '/training/leaderboards',
    title: 'Leaderboards',
    description: 'Training leaderboards across teams and time periods',
    layout: 'dashboard',
    header: 'full',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    breadcrumbParent: 'training',
    breadcrumbLabel: 'Leaderboards',
  },
];

// ============================================================================
// CONFERENCE / CALL PAGES
// ============================================================================

const conferenceRoutes: RouteConfig[] = [
  {
    id: 'call',
    path: '/calls/[callId]',
    title: (params) => `Call - ${params.params.callId?.slice(0, 8) || 'Active'}`,
    description: 'Live video conference session',
    layout: 'conference',
    header: 'conference',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'optional', // Citizens can join without auth via link
    hideBreadcrumb: true,
    preserveScrollPosition: false,
    nav: {
      label: 'Start Call',
      shortLabel: 'Call',
      icon: Phone,
      group: 'actions',
      positions: ['user-menu'],
      order: 1,
      hidden: true, // Don't show in nav, but used for "Start New Call" action
    },
  },
  {
    id: 'incident-report',
    path: '/incident-report',
    title: 'Generate Incident Report',
    description: 'Create incident report from call recording',
    layout: 'conference',
    header: 'conference',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Incident Report',
  },
  {
    id: 'incident-report-detail',
    path: '/incident-report/[callId]',
    title: (params) => `Incident Report - ${params.params.callId?.slice(0, 8) || '...'}`,
    description: 'View and edit incident report for a specific call',
    layout: 'conference',
    header: 'conference',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Incident Report',
  },
  {
    id: 'playback',
    path: '/playback/[roomId]',
    title: (params) => `Playback - ${params.params.roomId?.slice(0, 8) || '...'}`,
    description: 'Review call recording',
    layout: 'conference',
    header: 'conference',
    footer: 'full',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['officer', 'admin', 'supervisor'],
    breadcrumbParent: 'history',
    breadcrumbLabel: 'Playback',
  },
  {
    id: 'dispatch',
    path: '/dispatch',
    title: 'Dispatch Console',
    description: 'Real-time dispatch management',
    layout: 'conference',
    header: 'conference',
    footer: 'hidden',
    sidebar: 'none',
    portal: 'officer',
    auth: 'staff',
    requiredRoles: ['supervisor', 'admin'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Dispatch',
    nav: {
      label: 'Dispatch',
      icon: Radio,
      group: 'navigation',
      positions: ['sidebar'],
      order: 5,
    },
  },
];

// ============================================================================
// ADMIN PAGES
// ============================================================================

const adminRoutes: RouteConfig[] = [
  {
    id: 'adminpanel',
    path: '/adminpanel',
    title: 'Admin Panel',
    description: 'User and system administration',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Admin Panel',
    nav: {
      label: 'Admin Panel',
      icon: Shield,
      group: 'admin',
      positions: ['user-menu'],
      order: 1,
    },
  },
  {
    id: 'admin-prompt-catalog',
    path: '/adminpanel/prompts',
    title: 'Prompt Catalog',
    description: 'Browse AI prompt categories, owners, and reuse across repositories',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Prompt Catalog',
    nav: {
      label: 'Prompt Catalog',
      icon: Tags,
      group: 'analytics',
      positions: ['sidebar'],
      order: 9,
    },
  },
  // LLM Metrics Section
  {
    id: 'admin-llm-metrics',
    path: '/adminpanel/llm-metrics',
    title: 'LLM Metrics Dashboard',
    description: 'Monitor LLM usage, costs, and performance',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'LLM Metrics',
    nav: {
      label: 'LLM Metrics',
      icon: Activity,
      group: 'analytics',
      positions: ['sidebar'],
      order: 10,
    },
  },
  {
    id: 'admin-llm-metrics-costs',
    path: '/adminpanel/llm-metrics/costs',
    title: 'LLM Cost Breakdown',
    description: 'Analyze LLM costs by service, model, and function',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Costs',
    nav: {
      label: 'Costs',
      icon: DollarSign,
      group: 'analytics',
      positions: ['sidebar'],
      order: 11,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-requests',
    path: '/adminpanel/llm-metrics/requests',
    title: 'LLM Request Explorer',
    description: 'Browse and analyze individual LLM requests',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Requests',
    nav: {
      label: 'Requests',
      icon: List,
      group: 'analytics',
      positions: ['sidebar'],
      order: 12,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-conversations',
    path: '/adminpanel/llm-metrics/conversations',
    title: 'LLM Conversations',
    description: 'View multi-turn LLM conversation sessions',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Conversations',
    nav: {
      label: 'Conversations',
      icon: MessageSquare,
      group: 'analytics',
      positions: ['sidebar'],
      order: 13,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-realtime',
    path: '/adminpanel/llm-metrics/realtime',
    title: 'Real-time Monitor',
    description: 'Live LLM request monitoring',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Real-time',
    nav: {
      label: 'Real-time',
      icon: Activity,
      group: 'analytics',
      positions: ['sidebar'],
      order: 14,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-latency',
    path: '/adminpanel/llm-metrics/latency',
    title: 'Latency Analysis',
    description: 'LLM response time metrics and percentiles',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Latency',
    nav: {
      label: 'Latency',
      icon: Clock,
      group: 'analytics',
      positions: ['sidebar'],
      order: 15,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-throughput',
    path: '/adminpanel/llm-metrics/throughput',
    title: 'Throughput',
    description: 'LLM request volume and RPM metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Throughput',
    nav: {
      label: 'Throughput',
      icon: TrendingUp,
      group: 'analytics',
      positions: ['sidebar'],
      order: 16,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-errors',
    path: '/adminpanel/llm-metrics/errors',
    title: 'Error Rates',
    description: 'LLM error tracking and failure analysis',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Errors',
    nav: {
      label: 'Errors',
      icon: AlertTriangle,
      group: 'analytics',
      positions: ['sidebar'],
      order: 17,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-reconciliation',
    path: '/adminpanel/llm-metrics/reconciliation',
    title: 'AI Reconciliation',
    description: 'Compare internal AI meters with provider usage and cost buckets',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Reconciliation',
    nav: {
      label: 'Reconciliation',
      icon: GitBranch,
      group: 'analytics',
      positions: ['sidebar'],
      order: 18,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-models',
    path: '/adminpanel/llm-metrics/models',
    title: 'Model Settings',
    description: 'LLM model inventory and configuration',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Models',
    nav: {
      label: 'Models',
      icon: Database,
      group: 'analytics',
      positions: ['sidebar'],
      order: 19,
      isNested: true,
    },
  },
  {
    id: 'admin-llm-metrics-alerts',
    path: '/adminpanel/llm-metrics/alerts',
    title: 'Cost Alerts',
    description: 'LLM cost threshold alerts and notifications',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-llm-metrics',
    breadcrumbLabel: 'Alerts',
    nav: {
      label: 'Alerts',
      icon: Bell,
      group: 'analytics',
      positions: ['sidebar'],
      order: 20,
      isNested: true,
    },
  },
  // =========================================================================
  // Call Metrics Section (31 pages)
  // =========================================================================
  // Section 1: Overview (3 pages)
  {
    id: 'admin-call-metrics',
    path: '/adminpanel/call-metrics',
    title: 'Call Metrics Dashboard',
    description: 'Monitor call volume, quality, and AI feature utilization',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Call Metrics',
    nav: {
      label: 'Call Metrics',
      icon: PhoneCall,
      group: 'analytics',
      positions: ['sidebar'],
      order: 20,
    },
  },
  {
    id: 'admin-call-metrics-realtime',
    path: '/adminpanel/call-metrics/realtime',
    title: 'Real-time Monitor',
    description: 'Live call monitoring and active session tracking',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Real-time',
    nav: {
      label: 'Real-time',
      icon: Activity,
      group: 'analytics',
      positions: ['sidebar'],
      order: 21,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-trends',
    path: '/adminpanel/call-metrics/trends',
    title: 'Call Trends',
    description: 'Historical trend analysis and patterns',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Trends',
    nav: {
      label: 'Trends',
      icon: TrendingUp,
      group: 'analytics',
      positions: ['sidebar'],
      order: 22,
      isNested: true,
    },
  },
  // Section 2: Call Analytics (5 pages)
  {
    id: 'admin-call-metrics-volume',
    path: '/adminpanel/call-metrics/volume',
    title: 'Call Volume',
    description: 'Call volume metrics and distribution analysis',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Volume',
    nav: {
      label: 'Volume',
      icon: BarChart3,
      group: 'analytics',
      positions: ['sidebar'],
      order: 23,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-duration',
    path: '/adminpanel/call-metrics/duration',
    title: 'Call Duration',
    description: 'Call duration analysis and phase breakdown',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Duration',
    nav: {
      label: 'Duration',
      icon: Timer,
      group: 'analytics',
      positions: ['sidebar'],
      order: 24,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-outcomes',
    path: '/adminpanel/call-metrics/outcomes',
    title: 'Call Outcomes',
    description: 'Call outcome tracking and resolution rates',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Outcomes',
    nav: {
      label: 'Outcomes',
      icon: CheckCircle,
      group: 'analytics',
      positions: ['sidebar'],
      order: 25,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-connections',
    path: '/adminpanel/call-metrics/connections',
    title: 'Connection Metrics',
    description: 'WebRTC and SIP connection quality metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Connections',
    nav: {
      label: 'Connections',
      icon: Wifi,
      group: 'analytics',
      positions: ['sidebar'],
      order: 26,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-timeline',
    path: '/adminpanel/call-metrics/timeline',
    title: 'Call Timeline',
    description: 'Call phase analysis and timing breakdown',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Timeline',
    nav: {
      label: 'Timeline',
      icon: GitBranch,
      group: 'analytics',
      positions: ['sidebar'],
      order: 27,
      isNested: true,
    },
  },
  // Section 3: AI Features (8 pages)
  {
    id: 'admin-call-metrics-suggested-actions',
    path: '/adminpanel/call-metrics/suggested-actions',
    title: 'Suggested Actions',
    description: 'AI suggestion analytics and acceptance rates',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Suggested Actions',
    nav: {
      label: 'Suggested Actions',
      icon: Lightbulb,
      group: 'analytics',
      positions: ['sidebar'],
      order: 28,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-transcription',
    path: '/adminpanel/call-metrics/transcription',
    title: 'Transcription Analytics',
    description: 'Transcription quality and feedback metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Transcription',
    nav: {
      label: 'Transcription',
      icon: Mic,
      group: 'analytics',
      positions: ['sidebar'],
      order: 29,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-ai-notes',
    path: '/adminpanel/call-metrics/ai-notes',
    title: 'AI Notes Analytics',
    description: 'AI-generated notes usage and approval rates',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'AI Notes',
    nav: {
      label: 'AI Notes',
      icon: StickyNote,
      group: 'analytics',
      positions: ['sidebar'],
      order: 30,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-entities',
    path: '/adminpanel/call-metrics/entities',
    title: 'Entity Extraction',
    description: 'Entity extraction analytics and accuracy metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Entities',
    nav: {
      label: 'Entities',
      icon: Box,
      group: 'analytics',
      positions: ['sidebar'],
      order: 31,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-orchestrator',
    path: '/adminpanel/call-metrics/orchestrator',
    title: 'Orchestrator Analytics',
    description: 'Orchestrator node performance and completion rates',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Orchestrator',
    nav: {
      label: 'Orchestrator',
      icon: Workflow,
      group: 'analytics',
      positions: ['sidebar'],
      order: 32,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-canvas',
    path: '/adminpanel/call-metrics/canvas',
    title: 'Canvas Analytics',
    description: 'Canvas and annotation tool usage metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Canvas',
    nav: {
      label: 'Canvas',
      icon: PenTool,
      group: 'analytics',
      positions: ['sidebar'],
      order: 33,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-map',
    path: '/adminpanel/call-metrics/map',
    title: 'Map Analytics',
    description: 'Map feature usage and location metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Map',
    nav: {
      label: 'Map',
      icon: Map,
      group: 'analytics',
      positions: ['sidebar'],
      order: 34,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-file-browser',
    path: '/adminpanel/call-metrics/file-browser',
    title: 'File Browser Analytics',
    description: 'File browser usage and access patterns',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'File Browser',
    nav: {
      label: 'File Browser',
      icon: FolderOpen,
      group: 'analytics',
      positions: ['sidebar'],
      order: 35,
      isNested: true,
    },
  },
  // Section 4: Officer Interactions (5 pages)
  {
    id: 'admin-call-metrics-notes',
    path: '/adminpanel/call-metrics/notes',
    title: 'Notes Analytics',
    description: 'Officer note-taking patterns and metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Notes',
    nav: {
      label: 'Notes',
      icon: FileEdit,
      group: 'analytics',
      positions: ['sidebar'],
      order: 36,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-reports',
    path: '/adminpanel/call-metrics/reports',
    title: 'Reports Analytics',
    description: 'Report generation metrics and quality scores',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Reports',
    nav: {
      label: 'Reports',
      icon: FileText,
      group: 'analytics',
      positions: ['sidebar'],
      order: 37,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-feedback',
    path: '/adminpanel/call-metrics/feedback',
    title: 'Feedback Analytics',
    description: 'Officer feedback patterns and sentiment analysis',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Feedback',
    nav: {
      label: 'Feedback',
      icon: ThumbsUp,
      group: 'analytics',
      positions: ['sidebar'],
      order: 38,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-video',
    path: '/adminpanel/call-metrics/video',
    title: 'Video Analytics',
    description: 'Video feature usage and quality metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Video',
    nav: {
      label: 'Video',
      icon: Video,
      group: 'analytics',
      positions: ['sidebar'],
      order: 39,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-activity',
    path: '/adminpanel/call-metrics/activity',
    title: 'Activity Analytics',
    description: 'Officer activity tracking and engagement metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Activity',
    nav: {
      label: 'Activity',
      icon: Eye,
      group: 'analytics',
      positions: ['sidebar'],
      order: 40,
      isNested: true,
    },
  },
  // Section 5: Quality Metrics (4 pages)
  {
    id: 'admin-call-metrics-quality',
    path: '/adminpanel/call-metrics/call-quality',
    title: 'Call Quality',
    description: 'Call quality scoring and analysis',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Call Quality',
    nav: {
      label: 'Call Quality',
      icon: Star,
      group: 'analytics',
      positions: ['sidebar'],
      order: 41,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-surveys',
    path: '/adminpanel/call-metrics/surveys',
    title: 'Survey Analytics',
    description: 'Caller survey responses and satisfaction metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Surveys',
    nav: {
      label: 'Surveys',
      icon: ClipboardCheck,
      group: 'analytics',
      positions: ['sidebar'],
      order: 42,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-report-quality',
    path: '/adminpanel/call-metrics/report-quality',
    title: 'Report Quality',
    description: 'Report quality metrics and scoring',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Report Quality',
    nav: {
      label: 'Report Quality',
      icon: FileText,
      group: 'analytics',
      positions: ['sidebar'],
      order: 43,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-ai-performance',
    path: '/adminpanel/call-metrics/ai-performance',
    title: 'AI Performance',
    description: 'AI system accuracy and performance metrics',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'AI Performance',
    nav: {
      label: 'AI Performance',
      icon: Zap,
      group: 'analytics',
      positions: ['sidebar'],
      order: 44,
      isNested: true,
    },
  },
  // Section 6: Technical Performance (4 pages)
  {
    id: 'admin-call-metrics-latency',
    path: '/adminpanel/call-metrics/latency',
    title: 'Latency Metrics',
    description: 'System latency analysis and percentile distribution',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Latency',
    nav: {
      label: 'Latency',
      icon: Clock,
      group: 'analytics',
      positions: ['sidebar'],
      order: 45,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-errors',
    path: '/adminpanel/call-metrics/errors',
    title: 'Error Tracking',
    description: 'Error rates and failure analysis',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Errors',
    nav: {
      label: 'Errors',
      icon: AlertTriangle,
      group: 'analytics',
      positions: ['sidebar'],
      order: 46,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-throughput',
    path: '/adminpanel/call-metrics/throughput',
    title: 'Throughput Metrics',
    description: 'System throughput and capacity analysis',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Throughput',
    nav: {
      label: 'Throughput',
      icon: Gauge,
      group: 'analytics',
      positions: ['sidebar'],
      order: 47,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-resources',
    path: '/adminpanel/call-metrics/resources',
    title: 'Resource Usage',
    description: 'Resource utilization and cost estimation',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Resources',
    nav: {
      label: 'Resources',
      icon: Server,
      group: 'analytics',
      positions: ['sidebar'],
      order: 48,
      isNested: true,
    },
  },
  // Section 7: Configuration (2 pages)
  {
    id: 'admin-call-metrics-alerts',
    path: '/adminpanel/call-metrics/alerts',
    title: 'Alert Configuration',
    description: 'Configure threshold alerts and notifications',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Alerts',
    nav: {
      label: 'Alerts',
      icon: Bell,
      group: 'analytics',
      positions: ['sidebar'],
      order: 49,
      isNested: true,
    },
  },
  {
    id: 'admin-call-metrics-settings',
    path: '/adminpanel/call-metrics/settings',
    title: 'Dashboard Settings',
    description: 'Configure dashboard preferences and data retention',
    layout: 'settings',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'staff',
    requiredRoles: ['admin', 'supervisor', 'officer'],
    breadcrumbParent: 'admin-call-metrics',
    breadcrumbLabel: 'Settings',
    nav: {
      label: 'Settings',
      icon: Settings,
      group: 'analytics',
      positions: ['sidebar'],
      order: 50,
      isNested: true,
    },
  },
  // Navigation Section
  {
    id: 'admin-navigation',
    path: '/adminpanel/navigation',
    title: 'Navigation Management',
    description: 'Manage navigation structure and overrides',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Navigation',
  },
  {
    id: 'admin-navigation-items',
    path: '/adminpanel/navigation/items',
    title: 'Navigation Items',
    description: 'Manage navigation menu items',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-navigation',
    breadcrumbLabel: 'Items',
    isNested: true,
  },
  {
    id: 'admin-navigation-items-detail',
    path: '/adminpanel/navigation/items/[key]',
    title: (params) => `Edit: ${params.params.key || 'Navigation Item'}`,
    description: 'Edit navigation item details',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-navigation-items',
    breadcrumbLabel: (params) => params.params.key || 'Edit Item',
    isNested: true,
  },
  {
    id: 'admin-navigation-groups',
    path: '/adminpanel/navigation/groups',
    title: 'Navigation Groups',
    description: 'Manage navigation groups',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-navigation',
    breadcrumbLabel: 'Groups',
    isNested: true,
  },
  {
    id: 'admin-navigation-overrides',
    path: '/adminpanel/navigation/overrides',
    title: 'Navigation Overrides',
    description: 'Manage organization-specific navigation overrides',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-navigation',
    breadcrumbLabel: 'Overrides',
    isNested: true,
  },
  // Organizations Section
  {
    id: 'admin-organizations',
    path: '/adminpanel/organizations',
    title: 'Organizations',
    description: 'Manage agencies and departments',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Organizations',
    nav: {
      label: 'Organizations',
      icon: 'Building2',
      group: 'admin',
      positions: ['sidebar'],
      order: 3, // After Access Control (order 2)
    },
  },
  {
    id: 'admin-organizations-create',
    path: '/adminpanel/organizations/create',
    title: 'Create Organization',
    description: 'Create a new organization',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-organizations',
    breadcrumbLabel: 'Create',
    isNested: true,
  },
  {
    id: 'admin-organizations-detail',
    path: '/adminpanel/organizations/[id]',
    title: (params) => `Organization ${params.params.id?.slice(0, 8) || ''}`,
    description: 'View and edit organization details',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-organizations',
    breadcrumbLabel: (params) => `Org ${params.params.id?.slice(0, 8) || '...'}`,
    isNested: true,
  },
  {
    id: 'admin-organizations-levels',
    path: '/adminpanel/organizations/levels',
    title: 'Hierarchy Levels',
    description: 'Configure organization hierarchy levels',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-organizations',
    breadcrumbLabel: 'Hierarchy Levels',
    isNested: true,
  },
  // Repositories Section (Platform Admin)
  {
    id: 'admin-repositories',
    path: '/adminpanel/repositories',
    title: 'All Repositories',
    description: 'Platform-wide view of all vector repositories',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Repositories',
    nav: {
      label: 'All Repositories',
      icon: 'Database',
      group: 'admin',
      positions: ['sidebar'],
      order: 4, // After Organizations (order 3)
    },
  },
  // Other Admin Sections
  {
    id: 'admin-agencies',
    path: '/adminpanel/agencies',
    title: 'Organizations',
    description: 'Manage organization configurations',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Organizations',
  },
  {
    id: 'admin-audit',
    path: '/adminpanel/audit',
    title: 'Audit Log',
    description: 'View system audit logs',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Audit',
  },
  {
    id: 'admin-mcp',
    path: '/adminpanel/mcp',
    title: 'MCP Configuration',
    description: 'Configure Model Context Protocol settings',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'MCP Config',
  },
  // =========================================================================
  // Access Control Section
  // =========================================================================
  {
    id: 'admin-access-control',
    path: '/adminpanel/access-control',
    title: 'Access Control',
    description: 'User, role, and permission management',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Access Control',
    nav: {
      label: 'Access Control',
      icon: Shield,
      group: 'admin',
      positions: ['sidebar'],
      order: 2,
    },
  },
  // Users Section
  {
    id: 'admin-access-control-users',
    path: '/adminpanel/access-control/users',
    title: 'Users',
    description: 'Manage user accounts and access',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control',
    breadcrumbLabel: 'Users',
    isNested: true,
    nav: {
      label: 'Users',
      icon: Users,
      group: 'admin',
      positions: ['sidebar'],
      order: 1,
      isNested: true,
    },
  },
  {
    id: 'admin-access-control-users-invite',
    path: '/adminpanel/access-control/users/invite',
    title: 'Invite User',
    description: 'Invite a new user to the platform',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control-users',
    breadcrumbLabel: 'Invite',
    isNested: true,
  },
  {
    id: 'admin-access-control-users-detail',
    path: '/adminpanel/access-control/users/[userId]',
    title: (params) => `User ${params.params.userId?.slice(0, 8) || ''}`,
    description: 'View and edit user details',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control-users',
    breadcrumbLabel: (params) => `User ${params.params.userId?.slice(0, 8) || '...'}`,
    isNested: true,
  },
  // Invitations Section — the destination for the dashboard's "Pending
  // Invitations" tile. Registered here so breadcrumbs/layout resolve like every
  // other access-control page instead of falling through to defaults.
  {
    id: 'admin-access-control-invitations',
    path: '/adminpanel/access-control/invitations',
    title: 'Invitations',
    description: 'Sent invitations — pending, accepted, expired and revoked',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control',
    breadcrumbLabel: 'Invitations',
    isNested: true,
    nav: {
      label: 'Invitations',
      icon: Mail,
      group: 'admin',
      positions: ['sidebar'],
      order: 2,
      isNested: true,
    },
  },
  // Roles Section
  {
    id: 'admin-access-control-roles',
    path: '/adminpanel/access-control/roles',
    title: 'Roles',
    description: 'Manage roles and their permissions',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control',
    breadcrumbLabel: 'Roles',
    isNested: true,
    nav: {
      label: 'Roles',
      icon: UserCog,
      group: 'admin',
      positions: ['sidebar'],
      order: 2,
      isNested: true,
    },
  },
  {
    id: 'admin-access-control-roles-new',
    path: '/adminpanel/access-control/roles/new',
    title: 'Create Role',
    description: 'Create a new role with custom permissions',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control-roles',
    breadcrumbLabel: 'New Role',
    isNested: true,
  },
  {
    id: 'admin-access-control-roles-detail',
    path: '/adminpanel/access-control/roles/[id]',
    title: (params) => `Role ${params.params.id || ''}`,
    description: 'View and edit role details',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control-roles',
    breadcrumbLabel: (params) => params.params.id || 'Role Details',
    isNested: true,
  },
  // API Keys Section
  {
    id: 'admin-access-control-api-keys',
    path: '/adminpanel/access-control/api-keys',
    title: 'API Keys',
    description: 'Manage API keys and service accounts',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control',
    breadcrumbLabel: 'API Keys',
    isNested: true,
    nav: {
      label: 'API Keys',
      icon: KeyRound,
      group: 'admin',
      positions: ['sidebar'],
      order: 3,
      isNested: true,
    },
  },
  {
    id: 'admin-access-control-api-keys-new',
    path: '/adminpanel/access-control/api-keys/new',
    title: 'Create API Key',
    description: 'Generate a new API key',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control-api-keys',
    breadcrumbLabel: 'New Key',
    isNested: true,
  },
  {
    id: 'admin-access-control-api-keys-detail',
    path: '/adminpanel/access-control/api-keys/[id]',
    title: (params) => `API Key ${params.params.id?.slice(0, 8) || ''}`,
    description: 'View and edit API key details',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control-api-keys',
    breadcrumbLabel: (params) => `Key ${params.params.id?.slice(0, 8) || '...'}`,
    isNested: true,
  },
  // Permissions Browser
  {
    id: 'admin-access-control-permissions',
    path: '/adminpanel/access-control/permissions',
    title: 'Permissions Browser',
    description: 'Browse and search all available permissions',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control',
    breadcrumbLabel: 'Permissions',
    isNested: true,
    nav: {
      label: 'Permissions',
      icon: ShieldCheck,
      group: 'admin',
      positions: ['sidebar'],
      order: 4,
      isNested: true,
    },
  },
  // Templates Section (Platform Admin)
  {
    id: 'admin-access-control-templates',
    path: '/adminpanel/access-control/templates',
    title: 'Role Templates',
    description: 'Manage role templates for tenant provisioning',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin', 'admin-platform'],
    breadcrumbParent: 'admin-access-control',
    breadcrumbLabel: 'Templates',
    isNested: true,
    nav: {
      label: 'Templates',
      icon: Layers,
      group: 'admin',
      positions: ['sidebar'],
      order: 5,
      isNested: true,
    },
  },
  // Tiers Section (Platform Admin)
  {
    id: 'admin-access-control-tiers',
    path: '/adminpanel/access-control/tiers',
    title: 'Access Tiers',
    description: 'Manage subscription tiers and feature access',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin', 'admin-platform'],
    breadcrumbParent: 'admin-access-control',
    breadcrumbLabel: 'Tiers',
    isNested: true,
    nav: {
      label: 'Tiers',
      icon: ScrollText,
      group: 'admin',
      positions: ['sidebar'],
      order: 6,
      isNested: true,
    },
  },
  // Audit Log
  {
    id: 'admin-access-control-audit',
    path: '/adminpanel/access-control/audit',
    title: 'Access Audit Log',
    description: 'View access control audit history',
    layout: 'admin',
    header: 'full',
    footer: 'full',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'admin-access-control',
    breadcrumbLabel: 'Audit Log',
    isNested: true,
    nav: {
      label: 'Audit Log',
      icon: History,
      group: 'admin',
      positions: ['sidebar'],
      order: 7,
      isNested: true,
    },
  },
  // =========================================================================
  // Infrastructure Dashboards (iframe-embedded, platform admin only)
  // =========================================================================
  {
    id: 'admin-signoz',
    path: '/adminpanel/signoz',
    title: 'SigNoz Observability',
    description: 'Distributed tracing, metrics, and log analytics',
    layout: 'admin',
    header: 'full',
    footer: 'hidden',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'SigNoz',
    nav: {
      label: 'SigNoz',
      icon: Activity,
      group: 'infrastructure',
      positions: ['user-menu'],
      order: 1,
    },
  },
  {
    id: 'admin-traefik',
    path: '/adminpanel/traefik-dashboard',
    title: 'Traefik Dashboard',
    description: 'Ingress controller routes, services, and middleware',
    layout: 'admin',
    header: 'full',
    footer: 'hidden',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Traefik',
    nav: {
      label: 'Traefik',
      icon: Globe,
      group: 'infrastructure',
      positions: ['user-menu'],
      order: 2,
    },
  },
  {
    id: 'admin-portainer',
    path: '/adminpanel/portainer',
    title: 'Portainer',
    description: 'Container management and monitoring',
    layout: 'admin',
    header: 'full',
    footer: 'hidden',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Portainer',
    nav: {
      label: 'Portainer',
      icon: Box,
      group: 'infrastructure',
      positions: ['user-menu'],
      order: 3,
    },
  },
  {
    id: 'admin-livekit',
    path: '/adminpanel/livekit',
    title: 'LiveKit Dashboard',
    description: 'WebRTC server status, rooms, and participants',
    layout: 'admin',
    header: 'full',
    footer: 'hidden',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'LiveKit',
    nav: {
      label: 'LiveKit',
      icon: Video,
      group: 'infrastructure',
      positions: ['user-menu'],
      order: 4,
    },
  },
  {
    id: 'admin-supabase',
    path: '/adminpanel/supabase',
    title: 'Supabase Studio',
    description: 'Database management, auth, storage, and API explorer',
    layout: 'admin',
    header: 'full',
    footer: 'hidden',
    sidebar: 'admin',
    portal: 'admin',
    auth: 'admin',
    requiredRoles: ['admin'],
    breadcrumbParent: 'adminpanel',
    breadcrumbLabel: 'Supabase',
    nav: {
      label: 'Supabase',
      icon: Database,
      group: 'infrastructure',
      positions: ['user-menu'],
      order: 5,
    },
  },
];

// ============================================================================
// SETTINGS PAGES
// ============================================================================

const settingsRoutes: RouteConfig[] = [
  {
    id: 'settings',
    path: '/settings',
    title: 'Platform Settings',
    description: 'Configure platform settings and preferences',
    layout: 'settings',
    header: 'full',
    footer: 'hidden',
    sidebar: 'settings',
    portal: 'admin',
    auth: 'required',
    requiredRoles: ['admin', 'admin-platform', 'admin-tenant'],
    breadcrumbParent: 'dashboard',
    breadcrumbLabel: 'Settings',
    nav: {
      label: 'Platform Settings',
      icon: Settings,
      group: 'settings',
      positions: ['user-menu'],
      order: 2,
    },
    // Custom permission check for settings access
    permissionCheck: (context) => {
      if (!context.user) return false;

      // Platform admins always have access
      if (context.user.platformRole === 'admin-platform') return true;

      // Tenant admins can access if they have agencies
      if (context.user.platformRole === 'admin-tenant') {
        return (context.agencies?.length ?? 0) > 0;
      }

      // Legacy admin role
      if (context.user.role === 'admin') return true;

      return false;
    },
    unauthorizedRedirect: '/dashboard',
  },
  {
    id: 'settings-category',
    path: '/settings/[category]',
    title: (params) => {
      const category = params.params.category || 'Settings';
      return `${category.charAt(0).toUpperCase() + category.slice(1)} Settings`;
    },
    description: 'Configure category-specific settings',
    layout: 'settings',
    header: 'full',
    footer: 'hidden',
    sidebar: 'settings',
    portal: 'admin',
    auth: 'required',
    requiredRoles: ['admin', 'admin-platform', 'admin-tenant'],
    breadcrumbParent: 'settings',
    breadcrumbLabel: (params) => {
      const category = params.params.category || 'Category';
      return category.charAt(0).toUpperCase() + category.slice(1);
    },
    isNested: true,
  },
];

// ============================================================================
// DEVELOPER PORTAL PAGES (Public)
// ============================================================================

const developerPublicRoutes: RouteConfig[] = [
  {
    id: 'api-docs',
    path: '/api-docs',
    title: 'API Documentation',
    description: 'REST API documentation and reference',
    layout: 'developer',
    header: 'hidden',  // Hidden for full-page iframe
    footer: 'hidden',
    sidebar: 'none',
    portal: 'developer',
    auth: 'optional', // Allow browsing docs without auth
    breadcrumbParent: 'developers',
    breadcrumbLabel: 'API Docs',
    hideBreadcrumb: true,
    requiredFeatures: ['preview_beta'],
    nav: {
      label: 'API Docs',
      icon: BookOpen,
      group: 'developer',
      positions: ['user-menu'],
      order: 1,
    },
  },
  {
    id: 'ai-demo',
    path: '/ai-demo',
    title: 'AI Sandbox',
    description: 'Test the AI engine capabilities',
    layout: 'developer',
    header: 'hidden',  // Hidden for full-page iframe
    footer: 'hidden',
    sidebar: 'none',
    portal: 'developer',
    auth: 'optional', // Allow demo access without auth
    breadcrumbParent: 'developers',
    breadcrumbLabel: 'Sandbox',
    hideBreadcrumb: true,
    requiredFeatures: ['preview_prototype'],
    nav: {
      label: 'AI Sandbox',
      icon: Code,
      group: 'developer',
      positions: ['user-menu'],
      order: 2,
    },
  },
];

// ============================================================================
// DEVELOPER PORTAL PAGES (Authenticated - /developer/*)
// ============================================================================

const developerPortalRoutes: RouteConfig[] = [
  {
    id: 'developer-portal',
    path: '/developer',
    title: 'Developer Portal',
    description: 'API management, usage analytics, and developer tools',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbLabel: 'Developer Portal',
    nav: {
      label: 'Developer Portal',
      icon: Code,
      group: 'developer',
      positions: ['user-menu'],
      order: 1,
    },
  },
  {
    id: 'developer-keys',
    path: '/developer/keys',
    title: 'API Keys',
    description: 'Manage your API keys and credentials',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbParent: 'developer-portal',
    breadcrumbLabel: 'API Keys',
    nav: {
      label: 'API Keys',
      icon: Key,
      group: 'developer',
      positions: ['sidebar'],
      order: 2,
    },
  },
  {
    id: 'developer-keys-new',
    path: '/developer/keys/new',
    title: 'Create API Key',
    description: 'Generate a new API key',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbParent: 'developer-keys',
    breadcrumbLabel: 'New Key',
  },
  {
    id: 'developer-usage',
    path: '/developer/usage',
    title: 'Usage Analytics',
    description: 'View API usage statistics and trends',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbParent: 'developer-portal',
    breadcrumbLabel: 'Usage',
    nav: {
      label: 'Usage',
      icon: BarChart3,
      group: 'developer',
      positions: ['sidebar'],
      order: 3,
    },
  },
  {
    id: 'developer-metrics',
    path: '/developer/metrics',
    title: 'LLM Metrics',
    description: 'Monitor LLM costs, performance, and usage',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbParent: 'developer-portal',
    breadcrumbLabel: 'LLM Metrics',
    nav: {
      label: 'LLM Metrics',
      icon: Activity,
      group: 'developer',
      positions: ['sidebar'],
      order: 4,
    },
  },
  {
    id: 'developer-metrics-costs',
    path: '/developer/metrics/costs',
    title: 'Cost Breakdown',
    description: 'Analyze LLM costs by service, model, and function',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbParent: 'developer-metrics',
    breadcrumbLabel: 'Costs',
    nav: {
      label: 'Costs',
      icon: DollarSign,
      group: 'developer',
      positions: ['sidebar'],
      order: 5,
      isNested: true,
    },
  },
  {
    id: 'developer-metrics-requests',
    path: '/developer/metrics/requests',
    title: 'Request Explorer',
    description: 'Browse and analyze individual LLM requests',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbParent: 'developer-metrics',
    breadcrumbLabel: 'Requests',
    nav: {
      label: 'Requests',
      icon: List,
      group: 'developer',
      positions: ['sidebar'],
      order: 6,
      isNested: true,
    },
  },
  {
    id: 'developer-metrics-conversations',
    path: '/developer/metrics/conversations',
    title: 'Conversations',
    description: 'View multi-turn LLM conversation sessions',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbParent: 'developer-metrics',
    breadcrumbLabel: 'Conversations',
    nav: {
      label: 'Conversations',
      icon: MessageSquare,
      group: 'developer',
      positions: ['sidebar'],
      order: 7,
      isNested: true,
    },
  },
  // Note: Developer settings removed - use /profile for account settings
  {
    id: 'developer-billing',
    path: '/developer/billing',
    title: 'Billing',
    description: 'Manage subscription and billing information',
    layout: 'developer',
    header: 'developer',
    footer: 'full',
    sidebar: 'developer',
    portal: 'developer',
    auth: 'required',
    requiredRoles: ['developer', 'admin', 'admin-platform', 'owner'],
    breadcrumbParent: 'developer-portal',
    breadcrumbLabel: 'Billing',
    requiredFeatures: ['preview_beta'],
    nav: {
      label: 'Billing',
      icon: CreditCard,
      group: 'developer',
      positions: ['sidebar'],
      order: 9,
    },
  },
];

// ============================================================================
// COMBINED ROUTE CONFIGURATION
// ============================================================================

/**
 * All route configurations combined
 */
export const routeConfig: RouteConfig[] = [
  ...publicRoutes,
  ...authRoutes,
  ...citizenRoutes,
  ...dashboardRoutes,
  ...trainingRoutes,
  ...conferenceRoutes,
  ...adminRoutes,
  ...settingsRoutes,
  ...developerPublicRoutes,
  ...developerPortalRoutes,
];

// ============================================================================
// NAVIGATION GROUP LABELS
// ============================================================================

/**
 * Labels for navigation groups
 */
export const NAV_GROUP_LABELS: Record<string, string> = {
  default: '',
  actions: 'Quick Actions',
  navigation: 'Navigation',
  analytics: 'Analytics',
  admin: 'Administration',
  settings: 'Settings',
  developer: 'Developer',
};

/**
 * Sort order for navigation groups
 */
export const NAV_GROUP_ORDER: Record<string, number> = {
  actions: 0,
  navigation: 1,
  analytics: 2,
  admin: 3,
  settings: 4,
  developer: 5,
  default: 99,
};

// ============================================================================
// DEFAULT REDIRECTS
// ============================================================================

/**
 * Default redirect destinations based on auth requirement
 */
export const AUTH_REDIRECTS: Record<string, string> = {
  none: '/',
  optional: '/',
  required: '/login',
  staff: '/login',
  admin: '/login',
  platform: '/login',
  citizen: '/citizen/login',
  // Default for unauthenticated users (used by AuthGuard)
  unauthenticated: '/login',
};

/**
 * Default post-login destinations based on role
 */
export const ROLE_DEFAULT_ROUTES: Record<string, string> = {
  citizen: '/citizen',
  officer: '/dashboard',
  supervisor: '/dashboard',
  admin: '/dashboard',
  'admin-platform': '/dashboard',
  'admin-tenant': '/dashboard',
  developer: '/developer',
  owner: '/dashboard',
  trainee: '/training',
  member: '/dashboard',
};
